import { useState } from "react";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import CoOwnershipModal from "@/components/feature/CoOwnershipModal";
import {
  coOwnershipCampaigns,
  type CoOwnershipCampaign,
} from "@/mocks/coownership";

function fmtYen(n: number): string {
  if (n >= 1000000) return `¥${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `¥${(n / 1000).toFixed(0)}K`;
  return `¥${n}`;
}

type FilterValue = "all" | "open" | "almost" | "kominka";

export default function CoOwnership() {
  const [activeFilter, setActiveFilter] = useState<FilterValue>("all");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [interestModalOpen, setInterestModalOpen] = useState(false);
  const [interestCampaign, setInterestCampaign] = useState<CoOwnershipCampaign | null>(null);
  const { t } = useTranslation();

  const filters: { label: string; value: FilterValue }[] = [
    { label: t("co.filterAll"), value: "all" },
    { label: t("co.filterOpen"), value: "open" },
    { label: t("co.filterAlmost"), value: "almost" },
    { label: t("co.filterKominka"), value: "kominka" },
  ];

  const filtered = coOwnershipCampaigns.filter((c) => {
    if (activeFilter === "all") return true;
    if (activeFilter === "open") return c.ownersFilled < c.ownersWanted;
    if (activeFilter === "almost") return c.ownersFilled >= c.ownersWanted - 1;
    if (activeFilter === "kominka") return c.type === "Kominka";
    return true;
  });

  const selected = selectedId
    ? coOwnershipCampaigns.find((c) => c.id === selectedId) || null
    : null;

  const openInterest = (campaign: CoOwnershipCampaign) => {
    setInterestCampaign(campaign);
    setInterestModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-paper">
      <Navbar />
      <main className="pt-24 md:pt-32 pb-20">
        {/* Hero */}
        <div className="w-full px-4 md:px-8 lg:px-12 mb-8">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold mb-3">
            {t("co.pageLabel")}
          </p>
          <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-medium text-ink leading-tight mb-4">
            {t("co.pageTitle")}
            <br />
            <span className="italic font-normal text-ink/60">{t("co.pageTitleItalic")}</span>
          </h1>
          <p className="text-sm text-ink/50 max-w-xl leading-relaxed mb-6">
            {t("co.pageIntro")}
          </p>
        </div>

        {/* Legal banner */}
        <div className="w-full px-4 md:px-8 lg:px-12 mb-10">
          <div className="border-l-4 border-vermilion bg-[#fbeee6] p-4 md:p-5">
            <p className="text-xs text-ink/60 leading-relaxed">
              <strong className="text-ink/80">{t("co.legalBannerTitle")}</strong> {t("co.legalBannerText")}
            </p>
          </div>
        </div>

        <div className="w-full px-4 md:px-8 lg:px-12">
          {/* Filter chips */}
          <div className="flex flex-wrap gap-2 mb-8">
            {filters.map((f) => (
              <button
                key={f.value}
                onClick={() => {
                  setActiveFilter(f.value);
                  setSelectedId(null);
                }}
                className={`px-4 py-2 text-sm font-medium border transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeFilter === f.value
                    ? "bg-ink text-paper border-ink"
                    : "bg-white text-ink/60 border-hairline hover:border-ink/30"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Campaign grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 mb-12">
            {filtered.map((campaign) => {
              const spotsLeft = campaign.ownersWanted - campaign.ownersFilled;
              const pct = Math.round((campaign.ownersFilled / campaign.ownersWanted) * 100);
              return (
                <button
                  key={campaign.id}
                  onClick={() => setSelectedId(campaign.id)}
                  className={`text-left bg-white border transition-all duration-300 hover:-translate-y-1 cursor-pointer ${
                    selectedId === campaign.id ? "border-ink" : "border-hairline hover:border-ink/20"
                  }`}
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={campaign.image}
                      alt={campaign.title}
                      loading="lazy"
                      className="w-full h-48 object-cover"
                    />
                    {spotsLeft > 0 && (
                      <span className="absolute top-3 right-3 bg-vermilion text-paper text-xs font-semibold px-3 py-1.5">
                        {t("label.spotsLeft_other", { count: spotsLeft })}
                      </span>
                    )}
                    {spotsLeft === 0 && (
                      <span className="absolute top-3 right-3 bg-green-700 text-paper text-xs font-semibold px-3 py-1.5">
                        {t("label.full")}
                      </span>
                    )}
                  </div>
                  <div className="p-5">
                    <p className="text-xs text-ink/30 uppercase tracking-wider mb-1">
                      {campaign.region}
                    </p>
                    <h3 className="font-serif text-base font-medium text-ink mb-2 leading-snug">
                      {campaign.title}
                    </h3>
                    <p className="text-sm text-ink/40 mb-3 line-clamp-2">{campaign.description}</p>
                    <div className="flex items-baseline gap-2 mb-3">
                      <span className="font-serif text-lg font-semibold text-ink">
                        {fmtYen(campaign.allIn)}
                      </span>
                      <span className="text-xs text-ink/30">
                        {t("label.approx")} {fmtYen(Math.round(campaign.allIn / campaign.ownersWanted))}/{t("label.perShare")}
                      </span>
                    </div>
                    <div className="mb-2">
                      <div className="h-1.5 bg-paper-secondary rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#3F6F4F] rounded-full"
                          style={{ width: `${pct}%` }}
                        ></div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {Array.from({ length: campaign.ownersWanted }).map((_, i) => (
                        <span
                          key={i}
                          className={`w-2.5 h-2.5 rounded-full ${
                            i < campaign.ownersFilled
                              ? "bg-[#3F6F4F]"
                              : "bg-hairline"
                          }`}
                        ></span>
                      ))}
                      <span className="text-[10px] text-ink/30 ml-1">
                        {campaign.ownersFilled}/{campaign.ownersWanted} {t("label.allIn")}
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-12">
              <p className="text-ink/40 font-light">{t("co.noCampaigns")}</p>
              <button
                onClick={() => setActiveFilter("all")}
                className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-gold hover:text-ink transition-colors cursor-pointer"
              >
                {t("btn.showAll")}
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>
          )}

          {/* Detail view */}
          {selected && (
            <div className="border-t border-hairline pt-10 mb-12">
              <div className="flex flex-col lg:flex-row gap-10">
                {/* Main detail */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-xs text-ink/30 uppercase tracking-wider">
                      {selected.region}
                    </span>
                    <span className="text-xs text-ink/20">·</span>
                    <span className="text-xs text-ink/30 uppercase tracking-wider">
                      {selected.type}
                    </span>
                  </div>
                  <h2 className="font-serif text-2xl md:text-3xl font-medium text-ink mb-4 leading-tight">
                    {selected.title}
                  </h2>
                  <img
                    src={selected.image}
                    alt={selected.title}
                    loading="lazy"
                    className="w-full h-64 md:h-80 object-cover border border-hairline mb-8"
                  />

                  <section className="mb-8">
                    <h3 className="font-serif text-lg text-ink mb-3">
                      {t("co.buyingInto")}
                    </h3>
                    <p className="text-sm text-ink/60 leading-relaxed">
                      {selected.description}
                    </p>
                  </section>

                  <section className="mb-8">
                    <h3 className="font-serif text-lg text-ink mb-4">{t("co.proposedSplit")}</h3>
                    <div className="border border-hairline bg-white">
                      {selected.proposedSplit.map((row, idx) => (
                        <div
                          key={row.label}
                          className={`flex flex-col sm:flex-row sm:items-center justify-between gap-1 sm:gap-0 px-4 py-3 ${
                            idx !== 0 ? "border-t border-hairline" : ""
                          }`}
                        >
                          <span className="text-sm text-ink/60">{row.label}</span>
                          <span className="text-sm font-medium text-ink">{row.value}</span>
                        </div>
                      ))}
                    </div>
                  </section>

                  <section className="mb-8">
                    <h3 className="font-serif text-lg text-ink mb-4">
                      {t("co.stepsTitle")}
                    </h3>
                    <div className="space-y-4">
                      {selected.steps.map((s) => (
                        <div key={s.step} className="flex gap-4">
                          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-paper-secondary border border-hairline flex items-center justify-center text-xs font-semibold text-ink/50">
                            {s.step}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-ink mb-0.5">{s.title}</p>
                            <p className="text-sm text-ink/50 leading-relaxed">{s.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>

                  <section className="mb-8">
                    <div className="border-l-4 border-vermilion bg-[#fbeee6] p-4 md:p-5">
                      <h4 className="text-sm font-medium text-ink mb-2">{t("co.risksTitle")}</h4>
                      <ul className="space-y-2">
                        {selected.risks.map((r, i) => (
                          <li key={i} className="flex items-start gap-2 text-xs text-ink/60 leading-relaxed">
                            <span className="text-vermilion mt-0.5 flex-shrink-0">•</span>
                            {r}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </section>
                </div>

                {/* Sticky sidebar */}
                <aside className="w-full lg:w-72 flex-shrink-0">
                  <div className="lg:sticky lg:top-24 space-y-4">
                    <div className="border border-hairline bg-white p-5">
                      <p className="text-xs text-ink/30 uppercase tracking-wider mb-1">
                        {t("co.sidebarShare")}
                      </p>
                      <p className="font-serif text-2xl font-semibold text-ink mb-1">
                        {fmtYen(Math.round(selected.allIn / selected.ownersWanted))}
                      </p>
                      <p className="text-xs text-ink/30 mb-4">
                        {t("co.sidebarAllIn", { allIn: fmtYen(selected.allIn), count: selected.ownersWanted })}
                      </p>

                      <div className="mb-4">
                        <div className="h-2 bg-paper-secondary rounded-full overflow-hidden">
                          <div
                            className="h-full bg-[#3F6F4F] rounded-full"
                            style={{
                              width: `${Math.round(
                                (selected.ownersFilled / selected.ownersWanted) * 100
                              )}%`,
                            }}
                          ></div>
                        </div>
                        <p className="text-[10px] text-ink/30 mt-1">
                          {t("co.sidebarFilled", { filled: selected.ownersFilled, wanted: selected.ownersWanted })}
                        </p>
                      </div>

                      <button
                        onClick={() => openInterest(selected)}
                        className="block w-full text-center bg-vermilion text-paper text-sm font-medium px-4 py-2.5 hover:bg-vermilion/90 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        {t("btn.expressInterest")}
                      </button>
                    </div>

                    <div className="border-l-4 border-vermilion bg-[#fbeee6] p-4">
                      <p className="text-xs text-ink/60 leading-relaxed">
                        <strong className="text-ink/80">{t("co.guardrailSidebar")}</strong> {t("co.guardrailSidebarText")}
                      </p>
                    </div>
                  </div>
                </aside>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />

      {interestCampaign && (
        <CoOwnershipModal
          isOpen={interestModalOpen}
          onClose={() => setInterestModalOpen(false)}
          mode="interest"
          campaignTitle={interestCampaign.title}
        />
      )}
    </div>
  );
}